import { Component, OnChanges, SimpleChanges, TemplateRef, ViewContainerRef, inject, input, output, viewChild } from '@angular/core';
import { MatDialog, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { DeleteButtonWithConfirmationComponent } from '../delete-button-with-confirmation/delete-button-with-confirmation.component';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-image-uploader',
    template: `
    <form enctype="multipart/form-data" class="row justify-content-center">
      <section class="col-6 mt-3">
        <!-- <h5 class="text-center">{{ 'IMAGES_UPLOADER.SELECT_PROFILE_IMAGE' | translate }}</h5> -->
        <div class="d-flex justify-content-center">
          <div>
            <button class="mt-1" color="accent" mat-flat-button (click)="uploader.click()">
              {{ 'IMAGES_UPLOADER.UPLOAD_IMAGE' | translate }}
            </button>
          </div>
          <input #uploader type="file" hidden accept="image/*" (change)="onFileChange($event)" />
        </div>
        <ng-container #preview> </ng-container>
        <ng-template #noImageSelected>
          <p class="text-center mt-3">{{ 'IMAGES_UPLOADER.NO_IMAGE' | translate }}</p>
        </ng-template>
      </section>
    </form>

    <ng-template #imgTpl let-imgSrc>
      <div class="d-flex justify-content-center">
        <div
          class="my-2"
          id="img-container"
          [style.maxHeight]="imgMaxHeight() + 'px'"
          [style.maxWidth]="imgMaxWidth() + 'px'"
        >
          <img [src]="imgSrc" />
          <div class="overlay d-flex justify-content-center align-items-center">
            <div class="action">
              <app-delete-button-with-confirmation
                [message]="'IMAGES_UPLOADER.ASK_CONFIRMATION' | translate"
                (canDelete)="$event ? imageRemove.emit() : null"
              >
              </app-delete-button-with-confirmation>
              <!-- <button mat-icon-button color="warn" (click)="imageRemove.emit()">
                <mat-icon>delete</mat-icon>
              </button> -->
            </div>
          </div>
        </div>
      </div>
    </ng-template>

    <ng-template let-bounds #outOfBoundsTpl>
      <h3 mat-dialog-title>{{ 'IMAGES_UPLOADER.IMAGE_LIMITS' | translate }}</h3>
      <mat-dialog-content>
        <p>{{ 'IMAGES_UPLOADER.MAX_IMAGE_WIDTH' | translate }} {{ bounds.maxWidth }} pixel</p>
        <p>{{ 'IMAGES_UPLOADER.MAX_IMAGE_HEIGHT' | translate }} {{ bounds.maxHeight }} pixel</p>
      </mat-dialog-content>
      <mat-dialog-actions class="d-flex justify-content-center">
          <button mat-dialog-close mat-flat-button color="accent">{{ 'CLOSE' | translate }}</button>
      </mat-dialog-actions>
    </ng-template>
  `,
    styleUrls: ['./image-uploader.component.scss'],
    imports: [FormsModule, MatButton, DeleteButtonWithConfirmationComponent, MatDialogTitle, CdkScrollable, MatDialogContent, MatDialogActions, MatDialogClose, TranslateModule]
})
export class ImageUploaderComponent implements OnChanges {
  private dialog = inject(MatDialog);

  readonly imgMaxWidth = input<number>(200);

  readonly imgMaxHeight = input<number>(200);

  readonly showPreview = input<boolean>(false);

  readonly image = input.required<any>();

  readonly imageChange = output<File>();

  readonly imageRemove = output<void>();

  readonly preview = viewChild.required('preview', { read: ViewContainerRef });

  readonly imgTpl = viewChild.required('imgTpl', { read: TemplateRef });

  readonly outOfBoundsTpl = viewChild.required('outOfBoundsTpl', { read: TemplateRef });

  public currentImage!: File;

  private readonly _maxHeight: number = 400;
  private readonly _maxWidth: number = 400;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['image']?.firstChange) {
      this.displayPreview();
    }
  }

  onFileChange(data: Event) {
    const element = data.currentTarget as HTMLInputElement;
    const fileList: FileList | null = element.files;
    if (fileList) {
      const img = fileList[0];
      this.saveImageIfInBoudns(img, this._maxHeight, this._maxWidth);
    }
  }

  private displayPreview() {
    const image = this.image();
    if (image) {
      this.preview().clear();
      this.preview().createEmbeddedView(this.imgTpl(), {
        $implicit: image,
      });
    } else {
      this.preview().clear();
    }
  }

  private saveImageIfInBoudns(file: File, maxHeight: number, maxWidth: number): void {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const image = new Image();
      image.src = e.target.result;
      image.onload = (rs: any) => {
        const img_height = rs.currentTarget['height'];
        const img_width = rs.currentTarget['width'];

        if (img_height < maxHeight && img_width < maxWidth) {
          this.imageChange.emit(file);
          this.currentImage = file;
        } else {
          this.dialog.open(this.outOfBoundsTpl(), {
            data: {
              maxWidth,
              maxHeight,
            },
          });
          console.error('Image is out of bounds...');
        }
      };
    };

    reader.readAsDataURL(file);
  }
}
